import { z } from 'zod';

export type RestrictedMaterial = {
  isRestricted: boolean;
};

// Material stream type enum
export enum MaterialStreamTypeEnum {
  SINGLE = 'single',
  MIXED = 'mixed',
}

// Material stream type Zod schema
export const materialStreamTypeSchema = z.nativeEnum(MaterialStreamTypeEnum);

// Material stream type TypeScript type
export type MaterialStreamType = z.infer<typeof materialStreamTypeSchema>;

// Base material schema with all fields
export const materialZ = z.object({
  _id: z.string().optional(),
  name: z.string().min(1, 'Material name is required'),
  description: z.string().optional(),
  isRestricted: z.boolean().default(false),
  extraChargeInCents: z.number().min(0, 'Extra charge cannot be negative').default(0),
  recommendedContainerIds: z.array(z.string()).default([]),
  preAuthContainerWeightInTons: z.record(z.string(), z.number().min(0)).default({}),
  archived: z.boolean().default(false),
  displayOrder: z.number().min(1).default(1),
  streamType: materialStreamTypeSchema.default(MaterialStreamTypeEnum.MIXED),
  organizationId: z.string().optional(),
  isTemplate: z.boolean().default(false),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Form schema extends the base material schema
export const materialFormZ = materialZ;

// Create material schema (no _id required)
export const createMaterialZ = z.object({
  name: z.string().min(1, 'Material name is required'),
  description: z.string().optional(),
  isRestricted: z.boolean().default(false),
  extraChargeInCents: z.number().min(0, 'Extra charge cannot be negative').default(0),
  recommendedContainerIds: z.array(z.string()).default([]),
  preAuthContainerWeightInTons: z.record(z.string(), z.number().min(0)).default({}),
  archived: z.boolean().default(false),
  displayOrder: z.number().min(1).default(1),
  streamType: materialStreamTypeSchema.default(MaterialStreamTypeEnum.MIXED),
});

// Update material schema (_id required)
export const updateMaterialZ = z.object({
  _id: z.string(),
  name: z.string().min(1, 'Material name is required'),
  description: z.string().optional(),
  isRestricted: z.boolean(),
  extraChargeInCents: z.number().min(0, 'Extra charge cannot be negative'),
  recommendedContainerIds: z.array(z.string()),
  preAuthContainerWeightInTons: z.record(z.string(), z.number().min(0)),
  archived: z.boolean(),
  displayOrder: z.number().min(1),
  streamType: materialStreamTypeSchema,
});

export type Material = z.infer<typeof materialZ>;
export type MaterialForm = z.infer<typeof materialFormZ>;
export type CreateMaterial = z.infer<typeof createMaterialZ>;
export type UpdateMaterial = z.infer<typeof updateMaterialZ>;
